import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../Breadcrumb";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import { fetchPromoterVenues } from "../../services/promoterRequestService";

const PromoterVenuesList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortColumn, setSortColumn] = useState("totalSales");
  const [sortOrder, setSortOrder] = useState("desc");

  const colSpan = 4;

  const getVenueName = (item) =>
    item?.venueName ||
    item?.businessName ||
    item?.hostBusinessName ||
    item?.hostName ||
    item?.clubName ||
    item?.host?.businessName ||
    item?.host?.fullName ||
    "N/A";

  const getTotalCommission = (item) =>
    Number(
      item?.totalCommission ??
        item?.venueSales ??
        item?.revenue ??
        0
    );

    
  const getTotalBaseAmount = (item) =>
    Number(
      item?.totalBaseAmount ??
        0
    );

  const fetchList = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetchPromoterVenues(id, {
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery || "",
        status: statusFilter || "",
        sortBy: sortColumn,
        sortOrder,
      });

      const payload = response?.data || {};
      if (payload.status) {
        setItems(payload.data || []);
        setTotalItems(payload.pagination?.total || payload.total || 0);
        setTotalPages(
          payload.pagination?.totalPages || payload.totalPages || 1
        );
      } else {
        setItems([]);
        setTotalItems(0);
        setTotalPages(1);
        setErrorMessage(payload.message || "Unable to fetch venue list.");
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setErrorMessage(
        error?.response?.data?.message ||
          "Something went wrong while fetching venues."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [
    id,
    currentPage,
    itemsPerPage,
    searchQuery,
    statusFilter,
    sortColumn,
    sortOrder,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const renderSortIcons = (column) => (
    <span className="sort-colume">
      <Icon
        icon="mdi:arrow-up"
        className={`arrow ${
          sortColumn === column && sortOrder === "asc" ? "active" : ""
        }`}
      />
      <Icon
        icon="mdi:arrow-down"
        className={`arrow ${
          sortColumn === column && sortOrder === "desc" ? "active" : ""
        }`}
      />
    </span>
  );

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }

    return pages;
  };

  const formatStatus = (value) => {
    if (!value) return "N/A";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const getStatusClass = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return "bg-success-focus text-success-600 border border-success-main";
      case "pending":
        return "bg-warning-focus text-warning-600 border border-warning-main";
      case "rejected":
        return "bg-danger-focus text-danger-600 border border-danger-main";
      default:
        return "bg-neutral-200 text-secondary-light border";
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title2="Promoter Management"
        link2="/promoter"
        title3="View Promoter"
        link3={`/promoter/view-promoter/${id}`}
        title="Venues"
      />

      <div className="card h-100 p-0 radius-12">
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}

        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10 items per page</option>
              <option value="20">20 items per page</option>
              <option value="50">50 items per page</option>
              <option value="100">100 items per page</option>
            </select>

            <form
              className="navbar-search"
              style={{ position: "relative", minWidth: "280px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="bg-base h-40-px pe-40"
                name="search"
                placeholder="Search venue name"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: "100%",
                  minWidth: "280px",
                  paddingRight: searchQuery ? "5rem" : "2.5rem",
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="icon"
                style={{
                  position: "absolute",
                  right: searchQuery ? "3.25rem" : "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "30%",
                    transform: "translateY(-50%)",
                    fontSize: "1.25rem",
                    color: "#000",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </form>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    onClick={() => handleSort("venueName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Venue Name
                      {renderSortIcons("venueName")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("totalCommission")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Total Comission
                      {renderSortIcons("totalCommission")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("totalBaseAmount")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Sales Amount
                      {renderSortIcons("totalBaseAmount")}
                    </div>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center">
                      <span className="loader-15"></span>
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td>{getVenueName(item)}</td>
                      <td className="fw-semibold">
                        ${getTotalCommission(item).toLocaleString()}
                      </td>
                      <td className="fw-semibold">
                        ${getTotalBaseAmount(item).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${getStatusClass(
                            item.status
                          )}`}
                        >
                          {formatStatus(item.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="text-center py-5">
                      No venues found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              {totalItems === 0 ? (
                <>No entry to show</>
              ) : (
                <>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} entries
                </>
              )}
            </span>

            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage > 1 && setCurrentPage((prev) => prev - 1)
                  }
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>

              {getPaginationRange().map((pageNum) => (
                <li key={pageNum} className="page-item">
                  <button
                    type="button"
                    disabled={loading}
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === pageNum
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  type="button"
                  disabled={
                    currentPage === totalPages || totalPages === 0 || loading
                  }
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage < totalPages &&
                    setCurrentPage((prev) => prev + 1)
                  }
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PromoterVenuesList;
